import React, { useMemo } from 'react';
import {
	Alert,
	Checkbox,
	Space,
} from 'antd';
import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import {
	DepartmentKey,
	Departments,
	FeatureFlags,
} from '@shared/utils/constants';
import EditableCellTableRedux, { EditableColumns } from '@client/components/EditableTableRedux/EditableCellTableRedux';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	getUsers,
	updateUser,
	updateUserDepartments,
	inviteTeamMember,
	disableTeamMember,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { useAuth } from '@client/lib/auth';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import FormPopover from '@client/components/FormPopover';
import { Field } from '@client/components/SimpleForm';
import Button from '@client/components/Button';
import styles from './UserManagement.module.css';

export type UserSectionColumns = {
	id: number;
	username: string;
	chartering: boolean;
	operations: boolean;
	finance: boolean;
	hasVesselIndexAccess: boolean;
};

type InviteFormValues = {
	name: string;
	email: string;
	useOffice365: boolean;
	departments: DepartmentKey[];
};

const excludedDepartments = new Set(['management', 'admin']);

const departmentOptions = Object.values(Departments)
	.filter((dept) => !excludedDepartments.has(dept.key))
	.map((dept) => ({ label: dept.name, value: dept.key }));

const inviteFields: Field<InviteFormValues>[] = [
	{
		label: 'Name',
		name: 'name',
		type: 'text',
		required: true,
	},
	{
		label: 'Email',
		name: 'email',
		type: 'text',
		required: true,
	},
	{
		label: 'Use Office 365 SSO',
		name: 'useOffice365',
		type: 'switch',
		required: false,
	},
	{
		label: 'Departments',
		name: 'departments',
		type: 'select',
		required: true,
		options: departmentOptions,
		inputProps: {
			mode: 'multiple',
			placeholder: 'Select at least one department',
		},
	},
];

const UserManagement = () => {
	const { isManagement, userInfo, refreshAuthInfo } = useAuth();
	const currentUserId = 'id' in userInfo ? userInfo.id : null;
	const hasVesselIndex = useFeatureFlag(FeatureFlags.VESSELINDEX);

	const [userData, refreshData] = useFetchedState(async () => {
		const users = await getUsers();

		return users
			.filter((user) => !user.disabled)
			.map((user) => ({
				id: user.id,
				username: user.name ?? user.email ?? '',
				chartering: user.departments.includes('chartering'),
				operations: user.departments.includes('operations'),
				finance: user.departments.includes('finance'),
				hasVesselIndexAccess: user.hasVesselIndexAccess,
			}))
			.sort((a, b) => b.id - a.id);
	});

	const columns: EditableColumns<UserSectionColumns> = useMemo(() => [
		{
			title: 'Team Member',
			dataIndex: 'username',
			editable: false,
			type: 'text',
		},
		{
			title: 'Operations',
			dataIndex: 'operations',
			editable: true,
			width: 50,
			type: 'checkbox',
			align: 'center',
			renderInput: ({ value, onChange }) => (
				<div style={{ textAlign: 'center' }}>
					<Checkbox
						onChange={(e) => onChange(e.target.checked)}
						checked={value}
					/>
				</div>
			),
		},
		{
			title: 'Chartering',
			dataIndex: 'chartering',
			editable: true,
			width: 50,
			type: 'checkbox',
			renderInput: ({ value, onChange }) => (
				<div style={{ textAlign: 'center' }}>
					<Checkbox
						onChange={(e) => onChange(e.target.checked)}
						checked={value}
					/>
				</div>
			),
		},
		{
			title: 'Finance',
			dataIndex: 'finance',
			editable: true,
			width: 50,
			type: 'checkbox',
			renderInput: ({ value, onChange }) => (
				<div style={{ textAlign: 'center' }}>
					<Checkbox
						onChange={(e) => onChange(e.target.checked)}
						checked={value}
					/>
				</div>
			),
		},
		{
			title: 'VesselIndex access',
			dataIndex: 'hasVesselIndexAccess',
			editable: true,
			width: 80,
			type: 'checkbox',
			renderInput: ({ value, onChange }) => (
				<div style={{ textAlign: 'center' }}>
					<Checkbox
						onChange={(e) => onChange(e.target.checked)}
						checked={value}
						disabled={!hasVesselIndex}
					/>
				</div>
			),
		},
		...(isManagement ? [{
			title: '',
			editable: false as const,
			width: 50,
			render: (row: UserSectionColumns) => {
				if (row.id === currentUserId) {
					return null;
				}

				return (
					<Button
						type="text"
						danger
						icon={(<DeleteOutlined />)}
						confirmTitle="Are you sure you want to remove this user from your organisation?"
						onClick={async () => {
							try {
								await disableTeamMember(row.id);
								showSuccessNotification('User removed');
								await refreshData();
							} catch (_e) {
								showErrorNotification('Could not remove user');
							}
						}}
					/>
				);
			},
		}] : []),
	], [hasVesselIndex, isManagement, currentUserId, refreshData]);

	const handleVesselIndexChange = async (userId: number, value: boolean) => {
		try {
			await updateUser({
				userId,
				attributes: { hasVesselIndexAccess: value },
			});
		} catch (_e) {
			showErrorNotification('Could not update user');
		}
	};

	const handleChange = async (values: UserSectionColumns) => {
		const departments = Object.entries(values)
			.filter(([key, value]) => (key !== 'id' && key !== 'username') && value)
			.flat()
			.filter((d) => typeof d === 'string') as DepartmentKey[];

		if (departments.length < 1) {
			showErrorNotification('Team Members need to be part of at least one department');

			return;
		}

		try {
			await updateUserDepartments({ userId: values.id, departments });
			await refreshData();
			await refreshAuthInfo();
		} catch (_e) {
			showErrorNotification('Could not update permissions');
		}
	};

	const handleInvite = async (values: InviteFormValues) => {
		if (!values.departments || values.departments.length === 0) {
			showErrorNotification('New users must belong to at least one team');

			return;
		}

		try {
			await inviteTeamMember({
				name: values.name,
				email: values.email,
				external: !values.useOffice365,
				departments: values.departments,
			});
			showSuccessNotification(
				'Invitation sent',
				`An invitation was successfully sent to ${values.email}`,
			);
			await refreshData();
		} catch (_e) {
			showErrorNotification('Could not send invitation');
		}
	};

	return (
		<div className={styles.tableContainer}>
			<Space direction="vertical" style={{ width: '100%' }}>
				<Space style={{ justifyContent: 'space-between', width: '100%' }}>
					<Alert
						className={styles.alert}
						message="Team Members will only be able to switch between the departments defined below"
					/>
					{isManagement && (
						<FormPopover<InviteFormValues>
							onSubmit={handleInvite}
							buttonText="Add new team member"
							title="Invite a new team member"
							fields={inviteFields}
							buttonProps={{
								icon: (<UserAddOutlined />),
							}}
						/>
					)}
				</Space>
				<EditableCellTableRedux<UserSectionColumns>
					columns={columns}
					dataSource={userData ?? []}
					pagination={false}
					onRowChange={handleChange}
					onCellChange={(field, value, user) => {
						if (field === 'hasVesselIndexAccess') {
							handleVesselIndexChange(user.id, value);
						}
					}}
					onChange={() => null}
				/>
			</Space>
		</div>
	);
};

export default UserManagement;
